import { useEffect, useRef, useState } from 'react';
import styled from '@/styles';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import ScoreRing from './ScoreRing';
import TierPill from './TierPill';
import TierPopover from './TierPopover';
import StatusBadgeRow from './StatusBadgeRow';
import OnboardingChips from './OnboardingChips';
import StatCards from './StatCards';
import ActivityFeed from './ActivityFeed';
import LocationFlagModal from './LocationFlagModal';
import useLocationFlags from './useLocationFlags';
import ScoreHistoryModal from './ScoreHistoryModal';
import ActivationBreakdownModal from './ActivationBreakdownModal';
import RecentActivityModal from './RecentActivityModal';
import useDependabilityEvents from './useDependabilityEvents';
import {
  trackDependabilityBreakdownOpened,
  trackDependabilityFeedExpanded,
  trackDependabilityTierInfoOpened,
} from '@/util/vexo';

// Deliberately NOT elements/Card: the 2.0-Baseline card style is flat and
// outlined, which would mean negating Card's shadow and radius tokens.
const Wrapper = styled('div', {
  width: '100%',
  backgroundColor: 'white',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  borderRadius: '12px',
  border: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const TopSection = styled('div', {
  display: 'flex',
  gap: '32px',
  // ring column is vertically centered against the taller right column
  alignItems: 'center',
  flexWrap: 'wrap',
  paddingBottom: '24px',
  borderBottom: `1px solid ${dependabilityV2Colors.depBorder}`,
});
const ScoreColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
});
const RightColumn = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: '1 1 320px',
  minWidth: 0,
});
// The badges stand alone in the right column now that the tier pill has moved
// under the score ring. The group keeps its testid so the tier popover's
// collision heuristic (which treats the badges as an obstacle) and the specs can
// still find it; StatusBadgeRow's own Row handles wrapping.
const BadgeGroup = styled('div', {
  flex: '1 1 auto',
  minWidth: 0,
  // Stack the accountability badge row and (for activation workers) the
  // onboarding chips vertically, badges on top.
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  alignItems: 'flex-start',
});
const Breakdown = styled('button', {
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'center',
  color: dependabilityV2Colors.depPositive,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: '$sm',
  textDecoration: 'underline',
});

const FEED_PAGE_SIZE = 3;

interface Props {
  workerId: number;
  data: DependabilityV2;
}

const DependabilityV2Card = ({ workerId, data }: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  // GRAVY-10365. State lives here rather than in StatusBadgeRow so the badge stays a
  // presentational chip and the modal mounts as a sibling of the other two dialogs.
  const [locationFlagsOpen, setLocationFlagsOpen] = useState(false);
  // GRAVY-10347: the tier popover measures this wrapper to place itself clamped
  // inside the card (never over the header or under the sidebar).
  const tierAnchorRef = useRef<HTMLSpanElement>(null);

  // The tier popover is HOVER-driven: it shows while the pointer (or keyboard
  // focus) is on the pill or the popover, and hides shortly after both are left.
  // The popover is a child of this anchor, so hovering it still counts as
  // hovering the anchor (mouseleave doesn't fire when moving to a descendant).
  // The only bare spot is the small physical gap between the pill and the
  // floating popover — a short close delay bridges it so moving the pointer
  // across the gap to read the popover doesn't flicker it shut. A click still
  // opens it too (unchanged), which is the fallback for touch devices with no
  // hover.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Fires the "tier info opened" analytics once per open cycle. It moved here
  // from TierPill's onClick: hover/focus open the popover, so `expanded` was
  // already true by the time a click landed and the old click-gated call never
  // fired. The ref dedupes repeat triggers within one open (hover + focus +
  // click) and resets when the popover actually closes, so a genuine reopen
  // fires again.
  const tierInfoTrackedRef = useRef(false);
  const openPopover = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (!tierInfoTrackedRef.current) {
      tierInfoTrackedRef.current = true;
      trackDependabilityTierInfoOpened({
        worker_id: workerId,
        tier: data.tier,
      });
    }
    setPopoverOpen(true);
  };
  const closePopoverSoon = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setPopoverOpen(false);
      tierInfoTrackedRef.current = false;
    }, 150);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  // The inline feed shows only the first page; "View more" opens the
  // activity modal (view-activities frame) rather than growing the card.
  const { events, loading, loaded, error, loadPage, hasMore } =
    useDependabilityEvents(workerId, FEED_PAGE_SIZE);

  // GRAVY-10365. Loaded with the card, not on modal open: the badge needs the
  // count up front, and sharing one hook with the modal is what guarantees the
  // chip and the modal header agree.
  const locationFlags = useLocationFlags(workerId);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    locationFlags.load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerId]);

  // GRAVY-10429: activation workers get the dedicated Activation breakdown
  // modal; reliability workers keep ScoreHistoryModal. Guarded on the payload
  // block being present too, so a rolling-deploy backend that reports
  // scoring_mode='activation' without the new block still falls back to the
  // reliability breakdown (which renders the activation footer copy) rather
  // than opening an empty modal.
  const showActivationBreakdown =
    data.scoring_mode === 'activation' && !!data.activation_breakdown;
  // Figma 40013-12759: an activation worker's overview card shows onboarding
  // milestone chips in place of the reliability status badges.
  //
  // Product rule (clarified 2026-09-22): the chips depend on onboarding
  // PROGRESS, not only on shift count. They render only while the worker has
  // 0 completed shifts AND has not finished the onboarding steps:
  //
  //   shifts | onboarding steps | chips
  //   0      | not completed    | show
  //   0      | completed        | hide (same as the 1-4 shift case)
  //   1-4    | any              | hide
  //   5+     | any              | hide (reliability mode, no milestones)
  //
  // "Completed" is the onboarding STEPS block reporting complete — the
  // Onboarding chip reaching its full count — not every chip (background
  // check and training are separate gates that can stay open for a long
  // time). Scoring mode alone is not enough: the backend keeps a worker in
  // activation mode until 5 shifts, so gating on it painted the chips for
  // every 1-4 shift worker next to their real badges.
  const onboardingComplete = !!data.activation_breakdown?.onboarding?.complete;
  const showActivationChips =
    data.scoring_mode === 'activation' &&
    !!data.activation_breakdown?.milestones?.length &&
    data.completed_shifts_count === 0 &&
    !onboardingComplete;

  // GRAVY-10365 follow-up: a worker with ZERO completed shifts who is still
  // onboarding has never had the chance to no-show, arrive late, cancel late
  // or trip a location flag, so a full row of green "all clear" chips claims a
  // track record that does not
  // exist. Suppress the row for them.
  //
  // Gated on the SHIFT COUNT, not on `scoring_mode === 'activation'` / the "New
  // worker" tier. Those are a DIFFERENT concept that only partly overlaps: the
  // tier label holds until 5 completed shifts, so it also covers 1-4 shift
  // workers — and they HAVE worked, so their badges carry real information and
  // must keep rendering.
  //
  // The all-clean guard is not belt-and-braces. no_shows_count and
  // late_cancels_count are counts of worker_accountability_violations, which is
  // independent of timesheets: a worker who claims their first shift and
  // no-shows or late-drops it has 0 completed shifts AND a real violation. A
  // location flag likewise needs only a CHECK-IN, not an approved timesheet, and
  // late_count is counted over AdminOverviewService#stat_rows, which filters on
  // shifts.start_at only — `approved_at` is plucked but never filtered on, so a
  // late check-in still awaiting approval counts too. Every term below can
  // therefore be non-zero at 0 completed shifts: a bare
  // `completed_shifts_count === 0` would hide all of them, the same information
  // loss this rule deliberately avoids for 1-4 shift workers.
  //
  // `!locationFlags.error` is part of "clean" on purpose. useLocationFlags keeps
  // `error` distinct from empty so a failed fetch offers a retry rather than
  // reading as a spotless worker, and totalCount stays 0 on failure — so without
  // this term a 500 would hide the row, and with it the location-flag badge that
  // is the ONLY way into LocationFlagModal, where that retry lives. An admin
  // would be left with no accountability information and no way to recover it.
  const statusBadgesAllClean =
    data.badges.no_shows_count === 0 &&
    data.badges.late_count === 0 &&
    (data.badges.late_cancels_count ?? 0) === 0 &&
    data.badges.preferred_pools.length === 0 &&
    locationFlags.totalCount === 0 &&
    !locationFlags.error;
  // locationFlags.totalCount is 0 while its request is in flight, so the row
  // starts hidden and appears if flags arrive. That direction is deliberate: the
  // common case (a genuinely clean new worker) then never flickers, and only the
  // rare flagged-but-never-worked worker sees the row appear a beat late.
  // Waiting for `loaded` instead would flash the badges onto EVERY clean new
  // worker's profile and then yank them away.
  //
  // Product (retest of 2bc35b7, 2026-09-22): the never-worked-and-clean
  // suppression applies only while the worker is STILL ONBOARDING. Once the
  // onboarding steps are complete a 0-shift worker behaves like a 1-4 shift
  // worker: chips hidden, badges shown even when every count is 0.
  const showStatusBadges = !(
    data.completed_shifts_count === 0 &&
    statusBadgesAllClean &&
    !onboardingComplete
  );
  // milestones is optional on the payload (rolling-deploy back-compat) and no
  // deployed backend emits it yet, so "the chips take over the slot" is not
  // guaranteed. With neither child rendering, BadgeGroup would be an empty flex
  // item still contributing RightColumn's 16px gap above the stat cards — so
  // drop the wrapper instead of shipping a blank band.
  const showBadgeGroup = showStatusBadges || showActivationChips;

  return (
    <Wrapper data-testid="dependability-v2-card">
      <TopSection>
        <ScoreColumn data-testid="dependability-score-col">
          <ScoreRing score={data.score} tier={data.tier} />
          {/* The tier badge sits directly under the score ring, above the
              See-breakdown link. The popover is anchored to this wrapper. */}
          <span
            ref={tierAnchorRef}
            style={{ position: 'relative', display: 'inline-flex' }}
            onMouseEnter={openPopover}
            onMouseLeave={closePopoverSoon}
            onFocus={openPopover}
            onBlur={closePopoverSoon}
          >
            <TierPill
              tier={data.tier}
              label={data.tier_label}
              onInfoClick={openPopover}
              expanded={popoverOpen}
              accountabilityStatus={data.accountability_status}
              isSuspended={data.is_suspended}
              scoringMode={data.scoring_mode}
            />
            <TierPopover
              isOpen={popoverOpen}
              onRequestClose={() => setPopoverOpen(false)}
              anchorRef={tierAnchorRef}
              // fallback static side when no anchor is measured (unit tests)
              justify="right"
              boundaries={data.tier_boundaries}
              score={data.score}
              tier={data.tier}
              completedShifts={data.completed_shifts_count}
              scoringMode={data.scoring_mode}
              // New (activation) workers open a compact "Joined <date>" tooltip
              // instead of the tier bar (Figma 40013-12830). Absent when the
              // activation_breakdown block hasn't deployed → tier-view fallback.
              newWorkerJoinedAt={
                data.scoring_mode === 'activation'
                  ? data.activation_breakdown?.account_created_at
                  : undefined
              }
            />
          </span>
          <Breakdown
            type="button"
            onClick={() => {
              trackDependabilityBreakdownOpened({
                worker_id: workerId,
                kind: showActivationBreakdown ? 'activation' : 'reliability',
                tier: data.tier,
              });
              setHistoryOpen(true);
            }}
          >
            See breakdown
          </Breakdown>
        </ScoreColumn>
        <RightColumn>
          {/* Accountability badges (no-shows, late, late-cancels, preferred
              pools) render for every worker who has EITHER completed a shift or
              picked up a real signal — an activation worker (0-4 shifts) can
              still have all-time no-shows/late-drops an admin needs to see. Only
              the never-worked-and-spotless case is suppressed; see
              showStatusBadges. The onboarding chips render only for a 0-shift
              worker still onboarding (showActivationChips); the one case where
              both rows appear is such a worker with a real signal, and then the
              chips render BELOW the badges (badges first). */}
          {showBadgeGroup && (
            <BadgeGroup data-testid="dependability-badge-group">
              {/* GRAVY-10452: the badge takes TOTAL, not unresolved. It and the
                modal header it opens must be the same number — they read 3
                and 4 one click apart — and the header counts the list below
                it, which deliberately includes resolved entries so ops can
                revisit something already approved. Moving the header to
                unresolved instead would have left it undercounting its own
                rows. */}
              {showStatusBadges && (
                <StatusBadgeRow
                  badges={data.badges}
                  locationFlagCount={locationFlags.totalCount}
                  onLocationFlagsClick={() => setLocationFlagsOpen(true)}
                />
              )}
              {showActivationChips && (
                <OnboardingChips
                  milestones={data.activation_breakdown?.milestones ?? []}
                />
              )}
            </BadgeGroup>
          )}
          <StatCards stats={data.stats} />
        </RightColumn>
      </TopSection>
      <ActivityFeed
        events={events}
        hasMore={hasMore}
        loading={loading || !loaded}
        error={error}
        onRetry={() => loadPage(1)}
        onViewMore={() => {
          trackDependabilityFeedExpanded({ worker_id: workerId, page: 1 });
          setActivityOpen(true);
        }}
      />
      <RecentActivityModal
        workerId={workerId}
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
      />
      <LocationFlagModal
        isOpen={locationFlagsOpen}
        onClose={() => setLocationFlagsOpen(false)}
        flags={locationFlags.flags}
        totalCount={locationFlags.totalCount}
        windowDays={locationFlags.windowDays}
        loading={locationFlags.loading}
        loaded={locationFlags.loaded}
        error={locationFlags.error}
        retry={locationFlags.retry}
      />
      {showActivationBreakdown ? (
        <ActivationBreakdownModal
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          breakdown={data.activation_breakdown!}
        />
      ) : (
        <ScoreHistoryModal
          workerId={workerId}
          isOpen={historyOpen}
          onClose={() => setHistoryOpen(false)}
          signals={data.signals}
          completedShifts={data.completed_shifts_count}
          scoringMode={data.scoring_mode}
        />
      )}
    </Wrapper>
  );
};

export default DependabilityV2Card;
